'use client';

import { useEffect, useRef, useState } from 'react';

import { ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '../utils/cn';

interface PdfScrollViewProps {
	url: string;
	fileName: string;
	className?: string;
}

type LoadState = 'loading' | 'ready' | 'error';

const DPR_CAP = 2;

/**
 * Renders a PDF as a vertically scrollable stack of canvas pages via pdfjs-dist.
 *
 * iOS WebKit only ever paints the first page of an `<iframe src=*.pdf>` and
 * forwards no paging gestures, so mobile falls back to this. pdfjs-dist is
 * dynamically imported so it never lands in the main bundle. Pages render
 * progressively (first page first) into canvases sized to the container width.
 */
export const PdfScrollView = ({ url, fileName, className }: PdfScrollViewProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const pagesRef = useRef<HTMLDivElement>(null);
	const [state, setState] = useState<LoadState>('loading');

	useEffect(() => {
		let cancelled = false;
		const pagesEl = pagesRef.current;
		const containerEl = containerRef.current;
		if (!pagesEl || !containerEl) return;

		// Clear any canvases from a previous document (url change).
		pagesEl.replaceChildren();
		setState('loading');

		const render = async () => {
			try {
				const pdfjs = await import('pdfjs-dist');
				// Worker bundled by Next via import.meta.url resolution.
				pdfjs.GlobalWorkerOptions.workerSrc = new URL(
					'pdfjs-dist/build/pdf.worker.min.mjs',
					import.meta.url,
				).toString();

				const doc = await pdfjs.getDocument({ url }).promise;
				if (cancelled) return;

				const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
				const containerWidth = containerEl.clientWidth || 320;

				for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
					if (cancelled) return;
					const page = await doc.getPage(pageNum);
					if (cancelled) return;

					const baseViewport = page.getViewport({ scale: 1 });
					const scale = containerWidth / baseViewport.width;
					const viewport = page.getViewport({ scale: scale * dpr });

					const canvas = document.createElement('canvas');
					canvas.width = viewport.width;
					canvas.height = viewport.height;
					canvas.style.width = '100%';
					canvas.style.height = 'auto';
					canvas.style.display = 'block';
					canvas.className = 'rounded-lg bg-background';
					if (!canvas.getContext('2d')) continue;

					pagesEl.appendChild(canvas);
					await page.render({ canvas, viewport }).promise;

					// First page painted — show the scroller, hide the spinner.
					if (pageNum === 1 && !cancelled) setState('ready');
				}
			} catch {
				if (!cancelled) setState('error');
			}
		};

		void render();
		return () => {
			cancelled = true;
		};
	}, [url]);

	return (
		<div
			ref={containerRef}
			className={cn(
				'relative overflow-y-auto overscroll-contain rounded-lg bg-surface',
				className,
			)}
			style={{ WebkitOverflowScrolling: 'touch' }}
		>
			<div ref={pagesRef} className="flex flex-col gap-2 p-2" />

			{state === 'loading' && (
				<div className="absolute inset-0 flex items-center justify-center">
					<Loader2 className="size-6 animate-spin text-neutral-muted" />
				</div>
			)}

			{state === 'error' && (
				<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
					<span className="text-[13px] text-neutral-muted">
						Could not display this PDF.
					</span>
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-2 text-[13px] text-accent"
					>
						<ExternalLink className="size-4" />
						Open PDF
					</a>
					<span className="sr-only">{fileName}</span>
				</div>
			)}
		</div>
	);
};
